import {
  FormAnswer,
  FormAnswerDocumentStatusEnum,
  FormAnswerResp,
  FormTemplateQuestionFieldOption,
} from 'graphql-common';
import formatReadingDuration from '@lib/utils/formatReadingDuration';

type FormAnswerRespWithReadStatus = FormAnswerResp & {
  status?: FormAnswerDocumentStatusEnum | null;
  documentationVersionViewId?: string | null;
};

export interface DocumentAnswerStatus {
  option: FormTemplateQuestionFieldOption;
  docId: string;
  isRead: boolean;
  isReadPreviously: boolean;
  isReadAny: boolean;
  readingTime: string;
}

/**
 * Resolves, for each `Document`-kind option (sorted by position), the read
 * status + reading time from an answer's `resp`/`documentationVersionViews`.
 * Extracted so `DocumentResponseCell` and the daily-inspection compare table
 * (which needs the same per-document status without the document preview)
 * share one source of truth for the `documentationVersionViewId` matching.
 */
export default function getDocumentAnswerStatuses(
  options: FormTemplateQuestionFieldOption[],
  answer:
    Pick<FormAnswer, 'resp' | 'documentationVersionViews'> | null | undefined,
  readingDurationByDocumentationId?: Map<string, number>,
): DocumentAnswerStatus[] {
  const resp = (answer?.resp || []) as FormAnswerResp[];

  const respByOptionId = new Map<string, FormAnswerRespWithReadStatus>();
  resp.forEach((r) => {
    if (r.optionId)
      respByOptionId.set(r.optionId, r as FormAnswerRespWithReadStatus);
  });

  const readingDurationByViewId = new Map<string, number>();
  (answer?.documentationVersionViews || []).forEach((view) => {
    if (view?.id && typeof view.readingDurationSeconds === 'number') {
      readingDurationByViewId.set(view.id, view.readingDurationSeconds);
    }
  });

  const sorted = [...options].sort((a, b) => a.position - b.position);

  return sorted.reduce<DocumentAnswerStatus[]>((acc, option) => {
    const docId = option.optionDocumentation?.documentationId;
    if (!docId) return acc;

    const optResp = respByOptionId.get(option.id);
    const status = optResp?.status;
    const isRead = status === FormAnswerDocumentStatusEnum.Read;
    const isReadPreviously =
      status === FormAnswerDocumentStatusEnum.ReadPreviously;
    const isReadAny = isRead || isReadPreviously;

    const viewId = optResp?.documentationVersionViewId;
    const directHit = viewId ? readingDurationByViewId.get(viewId) : undefined;
    const fallbackHit =
      isReadAny && directHit === undefined
        ? readingDurationByDocumentationId?.get(docId)
        : undefined;

    acc.push({
      option,
      docId,
      isRead,
      isReadPreviously,
      isReadAny,
      readingTime: formatReadingDuration(directHit ?? fallbackHit ?? undefined),
    });
    return acc;
  }, []);
}
